import { observer } from 'mobx-react';
import React, { useCallback } from 'react';
import itemsStore from '../../store/itemsStore';
import { IItemSizeValidation } from '../../types/items';
import { LOCALES, MEASURE, measureValues } from 'src/constants/main';
import { ILangTextValidation, TTranslate } from 'src/types/main';
import { IProductV4Size } from 'src/types/product';
import MultiLanguageInput from 'src/view/components/Controls/MultiLanguageInput/MultiLanguageInput';
import DeleteRestoreBlock from 'src/view/components/DeleteRestoreBlock/DeleteRestoreBlock';
// eslint-disable-next-line max-len
import MenuManagTabItemCaloriesInput from 'src/view/screens/MenuManagementScreen/components/MenuManagTabItemCaloriesInput/MenuManagTabItemCaloriesInput';
import MenuManagTabItemPriceInput from 'src/view/screens/MenuManagementScreen/components/MenuManagTabItemPriceInput/MenuManagTabItemPriceInput';
import { getMeasureOption } from '~/helpers';
import { InputWithPickerInside } from '~/view/components/Controls/Inputs';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import './style.scss';

interface IProps {
  size: IProductV4Size;
}

const ItemSize: React.FC<IProps> = observer((props: IProps): JSX.Element => {
  const sizeValidation: IItemSizeValidation | undefined = itemsStore.validation.sizes.find((size) => size.id === props.size.id);
  const nameValidation: ILangTextValidation = {
    enValid: sizeValidation ? sizeValidation?.name.enValid : true,
    enErrorText: sizeValidation?.name.enErrorText,
    arValid: sizeValidation ? sizeValidation?.name.arValid : true,
    arErrorText: sizeValidation?.name.arErrorText,
  };

  const changeNameValue = useCallback(
    (value: string, currentLanguage: LOCALES) => {
      const name: TTranslate = { ...props.size.name, [currentLanguage]: value };
      itemsStore.updateSize(props.size.id, { name });
      // update validation
      if (sizeValidation && (!sizeValidation?.name.enValid || !sizeValidation?.name.arValid)) itemsStore.setValidation();
    },
    [sizeValidation, props.size.id, props.size.name]
  );

  const changeMeasureValue = useCallback(
    (weight: string | undefined) => {
      itemsStore.updateSize(props.size.id, { weight });
    },
    [props.size.id]
  );

  const changeMeasureLabel = useCallback(
    (options: IPickerOption[]) => {
      if (!options.length) return;
      itemsStore.updateSize(props.size.id, { unit: options[0].value as MEASURE });
    },
    [props.size.id]
  );

  const changeCalories = useCallback(
    (calories: number | null) => {
      itemsStore.updateSize(props.size.id, { calories });
    },
    [props.size.id]
  );

  const changePrice = useCallback(
    (price: string) => {
      itemsStore.updateSize(props.size.id, { price });
    },
    [props.size.id]
  );

  const handleDelete = () => {
    itemsStore.deleteSize(props.size.id);
  };

  return (
    <li className="item-size">
      {/* name block */}
      <MultiLanguageInput
        maxLength={50}
        enText={props.size.name.en}
        arText={props.size.name.ar}
        onChangeValue={changeNameValue}
        validation={nameValidation}
        className="item-size__input-name mr-3"
      />
      {/* portion */}
      <InputWithPickerInside
        type="floatNumber"
        value={props.size.weight}
        onChangeValue={changeMeasureValue}
        disableOnFocusOnInputWrapper
        className="item-size__input-portion flex-1 py-0 mr-3"
        contentClassName="full-width"
        pickerProps={{
          options: measureValues.map(getMeasureOption),
          selectedOptions: [getMeasureOption(props.size.unit)],
          onSelect: changeMeasureLabel,
        }}
      />
      {/* calories */}
      <MenuManagTabItemCaloriesInput
        calories={props.size.calories}
        validation={sizeValidation?.calories}
        changeCalories={changeCalories}
        setValidation={() => itemsStore.setValidation()}
        className="item-size__input-calories"
        wrapperClassName="mr-3"
      />
      {/* price */}
      <MenuManagTabItemPriceInput
        price={props.size.price}
        validation={sizeValidation?.price}
        changePrice={changePrice}
        setValidation={() => itemsStore.setValidation()}
        className="item-size__input-price"
      />
      {/* delete option button */}
      {itemsStore.item && itemsStore.item.sizes?.length > 1 ? (
        <div className="ml-4 mt-2">
          <DeleteRestoreBlock onDelete={handleDelete} iconClassName="color-gray2" />
        </div>
      ) : null}
    </li>
  );
});

export default ItemSize;
